// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import type { Alarm } from '../types/alarm';
import { isClearedState } from '../types/alarmFsm';

/** Asset id → its open alarms, from the alarm service. */
export type AlarmsByAsset = ReadonlyMap<string, Alarm[]>;

/** `P1 · Summary · 12m ago` — one hover line per open alarm. */
export const alarmLine = (alarm: Alarm): string =>
  [
    alarm.sev !== 'Unknown' ? alarm.sev : '',
    alarm.ruleTitle || alarm.summary || alarm.issue || alarm.id,
    alarm.raisedAgo === undefined
      ? ''
      : alarm.raisedAgo < 60
        ? `${alarm.raisedAgo}m ago`
        : `${Math.floor(alarm.raisedAgo / 60)}h ago`,
  ]
    .filter(Boolean)
    .join(' · ');

/**
 * Open alarms grouped by asset, keyed on both the equipment id and the human
 * equipment key so whichever one the twin uses as its id hits.
 */
export const alarmsByAsset = (alarms: readonly Alarm[]): Map<string, Alarm[]> => {
  const out = new Map<string, Alarm[]>();
  for (const alarm of alarms) {
    // A cleared alarm is open for the operator (it waits to be closed) but its
    // condition is gone, so it no longer flags the asset.
    if (alarm.closed || isClearedState(alarm.status)) continue;
    for (const key of Array.from(
      new Set([alarm.assetId, alarm.asset].filter(Boolean) as string[])
    )) {
      const list = out.get(key) ?? [];
      list.push(alarm);
      out.set(key, list);
    }
  }
  return out;
};

/** Every open alarm on any of `ids`, deduped by row. */
export const alarmsFor = (alarmsOf: AlarmsByAsset | undefined, ids: readonly string[]): Alarm[] => {
  const seen = new Set<string>();
  const out: Alarm[] = [];
  for (const id of ids) {
    for (const alarm of alarmsOf?.get(id) ?? []) {
      if (seen.has(alarm.uid)) continue;
      seen.add(alarm.uid);
      out.push(alarm);
    }
  }
  return out;
};

/** The hover title: the element's own line, then one line per alarm. */
export const withAlarmLines = (
  title: string | undefined,
  lines: readonly string[] | undefined
): string | undefined => {
  if (!lines?.length) return title;
  return [title, ...lines].filter(Boolean).join('\n');
};
